import React, { useState, useContext, useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import LeftMenu from "./components/LeftMenu";
import AppNavbar from "./components/Navbar";
import Footer from "./components/Footer";
import { AuthContext } from "../../context/AuthContext";
import AccessControlService from "../../services/AccessControlService";
import SettingsService from "../../services/SettingsService";
import { API_BASE_URL } from "../../configs/apiConfig";
import "./styles/AdminDashboard.css";
import { getPageTitle } from '../../configs/pageTitles';

const AdminDashboard = ({ useStaticMenu }) => {
    const { user } = useContext(AuthContext);
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [organizationName, setOrganizationName] = useState('Your Organization');
    const [organizationLogo, setOrganizationLogo] = useState(null);
    const { pageName } = useParams();
    const currentPageTitle = getPageTitle(pageName);

    // Remove modal theme attributes and theme mode when entering admin dashboard
    useEffect(() => {
        // Remove CSS custom properties
        document.documentElement.style.removeProperty('--nav-bg-color');
        document.documentElement.style.removeProperty('--opacity');
        document.documentElement.style.removeProperty('--blur');
        document.documentElement.style.removeProperty('--base-color');
        document.documentElement.style.removeProperty('--base-color-rgb');
        document.documentElement.style.removeProperty('--accent-color');
        
        
        // Remove theme mode
        document.documentElement.removeAttribute('data-theme');
    }, []);

    // Static admin menu items
    const staticAdminMenu = [
        {
            id: 100,
            name: "User Management",
            code: "userMgnCat",
            iconCode: "FaUsers",
            children: [
                { id: 1, name: "User Management", code: "userMgn", iconCode: "FaUsers" },             
                
            ]
        },
        {
            id: 101,
            name: "Module Management",
            code: "moduleMgnCat",
            iconCode: "FaFileAlt",
            children: [
                { id: 3, name: "Page Management", code: "pageMgn", iconCode: "FaFileAlt" },
                { id: 4, name: "Access Control", code: "accessControl", iconCode: "FaLock" },
              
            ]
        },  
         {
            id: 102,
            name: "Dashboard Management",
            code: "dashboardMgnCat",
            iconCode: "FaFileAlt",
            children: [
            
                { id: 5, name: "Announcements", code: "announcements", iconCode: "FaBullhorn" },
                { id: 6, name: "Desktop Management", code: "desktopManagement", iconCode: "FaDesktop" }
            ]
        },
        {
            id: 103,
            name: "Workflow & Process",
            code: "workflowProcessCat",
            iconCode: "FaProjectDiagram",
            children: [
                { id: 7, name: "Process Types", code: "processTypes", iconCode: "FaFileAlt" },
                { id: 8, name: "Workflows", code: "workflows", iconCode: "FaProjectDiagram" },
                { id: 9, name: "Workflow Assignments", code: "workflowAssignments", iconCode: "FaExchangeAlt" },
                { id: 10, name: "Tasks", code: "tasks", iconCode: "FaTasks" },
                { id: 11, name: "My Tasks", code: "myTasks", iconCode: "FaTasks" },
                { id: 12, name: "My Requests", code: "myRequests", iconCode: "FaFileAlt" },
                { id: 23, name: "Auditor Assignments", code: "auditorAssignments", iconCode: "FaCheckCircle" }
            ]
        },
      
        {
            id: 104,
            name: "HR & Benefits",
            code: "hrBenefits",
            iconCode: "FaGift",
            children: [
                { id: 2, name: "Organizational Chart", code: "orgChart", iconCode: "FaSitemap" },
                { id: 11, name: "Appointment", code: "appointment", iconCode: "FaCalendarCheck" },
                { id: 12, name: "Appointment Parameters", code: "appointmentParameters", iconCode: "FaCalendarAlt" },

                { id: 13, name: "Leave Request", code: "leaveRequests", iconCode: "FaCalendarMinus" },
                { id: 14, name: "Leave Settings", code: "leaveSettings", iconCode: "FaCalendarAlt" },
                { id: 15, name: "Bonus Requests", code: "bonusRequests", iconCode: "FaGift" },
                { id: 16, name: "Bonus Types", code: "bonusTypes", iconCode: "FaGift" },
                
                { id: 17, name: "Document Types", code: "userDocumentType", iconCode: "FaFileAlt" }
               
              
            ]
        },
        {
            id: 105,
            name: "Communication",
            code: "communication",
            iconCode: "FaEnvelope",
            children: [
                { id: 19, name: "Message Categories", code: "messageCategories", iconCode: "FaEnvelope" },
                { id: 20, name: "Messages", code: "messages", iconCode: "FaEnvelope" },
                { id: 21, name: "Tickets", code: "tickets", iconCode: "FaTicketAlt" },
                { id: 22, name: "Ticket Types", code: "ticketTypes", iconCode: "FaFileAlt" }
            ]
        },
        {
            id: 3,
            name: "Settings",
            code: "settings",
            iconCode: "FaCog"
        }
    ];

    // Fetch organization settings
    useEffect(() => {
        const fetchOrganizationSettings = async () => {
            try {
                const response = await SettingsService.getSetting('organization');
                if (response && response.value) {
                    const orgSettings = JSON.parse(response.value);
                    setOrganizationName(orgSettings.organizationName || 'Your Organization');
                    
                    // Use OrganizationLogo if available, fall back to organizationLogo
                    const logoPath = orgSettings.OrganizationLogo || orgSettings.organizationLogo;
                    if (logoPath) {
                        // If the path is relative, prepend the API base URL
                        const logoUrl = logoPath.startsWith('http') 
                            ? logoPath 
                            : `${API_BASE_URL}${logoPath}`;
                        setOrganizationLogo(logoUrl);
                    }
                }
            } catch (error) {
                // Handle error silently - fallback to default values
            }
        };
        
        fetchOrganizationSettings();
    }, []);

    useEffect(() => {
        const loadMenuItems = async () => {
            setLoading(true);
            try {
                if (useStaticMenu) {
                    // Add a small delay to show loading state even for static menu
                    await new Promise(resolve => setTimeout(resolve, 300));
                    setMenuItems(staticAdminMenu);
                } else if (user?.userId) {
                    const accessiblePages = await AccessControlService.getUserAccessiblePagesTree(user.userId);
                    
                    // Format pages to match staticAdminMenu structure
                    const formattedPages = accessiblePages.map(page => ({
                        id: page.id,
                        name: page.name,
                        code: page.code,
                        iconCode: page.iconCode,
                        children: page.children
                    }));

                    setMenuItems(formattedPages);
                } else {
                    setMenuItems([]);
                }
            } catch (error) {
                // Handle error by setting empty menu items
                setMenuItems([]);
            } finally {
                setLoading(false);
            }
        };

        loadMenuItems();
    }, [user?.userId, useStaticMenu]);

    // State to track sidebar collapse
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Toggle sidebar state
    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    return (
        <div className="dashboard-container admin-dashboard">
            {/* Left Menu Section */}
            <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
                <LeftMenu 
                    menuItems={menuItems} 
                    onToggle={toggleSidebar}
                    loading={loading}
                    organizationLogo={organizationLogo}
                    organizationName={organizationName}
                    isCollapsed={isCollapsed}
                    isStatic={useStaticMenu}
                />
            </div>

            {/* Main Content Section */}
            <div className="dashboard-content">
                <AppNavbar pageTitle={currentPageTitle} />
                <div className="page-content">
                    <Outlet />
                </div>
                <Footer />

            </div>
        </div>
    );
};

export default AdminDashboard;
